// FFM Category Configuration
// Single source of truth for all COA → FFM mapping categories.
// Every Xero nominal maps to exactly one of these categories.
// The output P&L structure is fixed regardless of client COA complexity.
export const FFM_CATEGORY_KEYS = [
  'revenue',
  'cost_of_sales',
  'staff_costs',
  'owner_salary',
  'owner_pension',
  'premises',
  'marketing',
  'professional_fees',
  'it_technology',
  'vehicles',
  'finance_costs',
  'depreciation',
  'other_operating',
  'ignore',
] as const;
export type FFMCategory = typeof FFM_CATEGORY_KEYS[number];
export interface FFMCategoryMeta {
  key: FFMCategory;
  label: string;
  description: string;
  group: 'revenue' | 'direct' | 'operating' | 'owner' | 'finance' | 'ignore';
  colour: string;
}
// Display metadata for each category
export const FFM_CATEGORIES: Record<FFMCategory, FFMCategoryMeta> = {
  revenue: {
    key: 'revenue',
    label: 'Revenue',
    description: 'All income streams — sales, fees, charges, other income',
    group: 'revenue',
    colour: '#6EE7B7',
  },
  cost_of_sales: {
    key: 'cost_of_sales',
    label: 'Cost of Sales',
    description: 'Direct costs only — materials, subcontractors, direct labour, stock',
    group: 'direct',
    colour: '#93C5FD',
  },
  staff_costs: {
    key: 'staff_costs',
    label: 'Staff Costs',
    description: 'Employee wages, salaries, employer NI, staff pension — excludes owner',
    group: 'operating',
    colour: '#1E3A5F',
  },
  owner_salary: {
    key: 'owner_salary',
    label: 'Owner Salary',
    description: 'Director remuneration, salary, drawings — shown separately for lever analysis',
    group: 'owner',
    colour: '#C9A84C',
  },
  owner_pension: {
    key: 'owner_pension',
    label: 'Owner Pension',
    description: 'Employer pension contributions for the director — CT-efficient, shown separately',
    group: 'owner',
    colour: '#1E3A5F',
  },
  premises: {
    key: 'premises',
    label: 'Premises',
    description: 'Rent, rates, utilities, electricity, gas, water, cleaning, buildings insurance',
    group: 'operating',
    colour: '#A78BFA',
  },
  marketing: {
    key: 'marketing',
    label: 'Marketing',
    description: 'Advertising, events, PR, SEO, PPC, social media, sponsorship, brand',
    group: 'operating',
    colour: '#FCA5A5',
  },
  professional_fees: {
    key: 'professional_fees',
    label: 'Professional Fees',
    description: 'Accountancy, legal, compliance, bought-in consulting, audit, advisory',
    group: 'operating',
    colour: '#6EE7B7',
  },
  it_technology: {
    key: 'it_technology',
    label: 'IT & Technology',
    description: 'Software, SaaS subscriptions, cloud services, IT support, hardware, telecoms',
    group: 'operating',
    colour: '#93C5FD',
  },
  vehicles: {
    key: 'vehicles',
    label: 'Vehicles & Travel',
    description: 'Motor expenses, fuel, mileage, car/van lease, fleet costs, transport',
    group: 'operating',
    colour: '#FCD34D',
  },
  finance_costs: {
    key: 'finance_costs',
    label: 'Finance Costs',
    description: 'Bank charges, loan interest, HP interest, overdraft charges, factoring fees',
    group: 'finance',
    colour: '#EF4444',
  },
  depreciation: {
    key: 'depreciation',
    label: 'Depreciation',
    description: 'Depreciation and amortisation — non-cash, added back for cash analysis',
    group: 'finance',
    colour: '#9CA3AF',
  },
  other_operating: {
    key: 'other_operating',
    label: 'Other Operating',
    description: 'Training, entertaining, insurance, telephone, print, postage, miscellaneous',
    group: 'operating',
    colour: '#D1D5DB',
  },
  ignore: {
    key: 'ignore',
    label: 'Ignore',
    description: 'VAT control, inter-company, suspense, balance sheet nominals, opening balances',
    group: 'ignore',
    colour: '#E5E7EB',
  },
};
// ─── Auto-suggestion engine ───────────────────────────────────────────────────
// Four layers: balance-sheet guard → Xero account type → code range → keyword.
// Returns suggested category + confidence score (0–1).
// Layer 0: Balance sheet Xero types — never appear in a P&L and should be
// skipped with very high confidence. Evaluated before anything else.
const BALANCE_SHEET_TYPES = [
  'BANK',
  'CURRENT',
  'CURRLIAB',
  'EQUITY',
  'FIXED',
  'INVENTORY',
  'NONCURRENT',
  'OTHERASSET',
  'OTHERLIAB',
  'PREPAYMENT',
  'TERMLIAB',
  'PAYGLIABILITY',
  'SUPERANNUATION',
  'VATLIABILITY',
];
// Layer 1: Xero account type direct mapping for P&L types
const TYPE_MAP: Record<string, FFMCategory> = {
  'REVENUE':      'revenue',
  'SALES':        'revenue',
  'OTHER_INCOME': 'revenue',
  'OTHERINCOME':  'revenue',
  'DIRECTCOSTS':  'cost_of_sales',
  'COGS':         'cost_of_sales',
  'DEPRECIATN':   'depreciation',
  'OVERHEADS':    'other_operating',
  'EXPENSE':      'other_operating',
};
// Layer 2: Xero account code ranges (standard COA)
function suggestByCode(code: string): FFMCategory | null {
  const n = parseInt(code, 10);
  if (isNaN(n)) return null;
  if (n >= 200 && n <= 299) return 'revenue';
  if (n >= 300 && n <= 399) return 'cost_of_sales';
  if (n >= 700 && n <= 799) return 'depreciation';
  if (n >= 800 && n <= 899) return 'finance_costs';
  return null;
}
// Layer 2: Keyword matching (handles customised COAs).
// All entries share the same 0.85 confidence per the mapping spec, so the
// FIRST matching group in list order wins. Owner-specific and depreciation
// groups are therefore placed ahead of the generic staff / revenue / expense
// groups so "director pension" beats "pension" → staff_costs, and so on.
// Matching is case-insensitive on both the account name and the pattern.
const KEYWORD_MAP: Array<{ patterns: string[]; category: FFMCategory; confidence: number }> = [
  // Owner pension — must precede staff_costs so "director pension" wins.
  {
    patterns: [
      'director pension', 'directors pension',
      'employer pension', 'pension contribution', 'employer contribution',
      'sipp', 'directors sipp', 'executive pension',
    ],
    category: 'owner_pension', confidence: 0.85,
  },
  // Owner salary — must precede staff_costs so "director salary" wins.
  {
    patterns: [
      'director salary', 'directors salary',
      'directors remuneration', 'director remuneration',
      'management salary', 'owner salary', 'partner salary',
      'shareholder salary', 'director drawings', 'directors fees',
    ],
    category: 'owner_salary', confidence: 0.85,
  },
  // Depreciation
  {
    patterns: [
      'depreciation', 'amortisation', 'amortization',
      'capital allowances', 'write off', 'write-off', 'impairment',
      'asset write down', 'loss on disposal',
    ],
    category: 'depreciation', confidence: 0.85,
  },
  // Finance costs
  {
    patterns: [
      'bank charges', 'bank fees', 'interest payable',
      'loan interest', 'finance charges', 'merchant fees', 'stripe fees',
      'paypal fees', 'credit card fees', 'factoring', 'overdraft interest',
      'finance lease', 'bad debt', 'debt recovery', 'exchange losses',
    ],
    category: 'finance_costs', confidence: 0.85,
  },
  // Cost of sales — before staff_costs so "direct wages" wins.
  {
    patterns: [
      'cost of sales', 'direct costs', 'direct cost',
      'materials', 'subcontractor', 'subcontract', 'direct labour',
      'direct wages', 'purchases', 'stock', 'freight', 'delivery',
      'courier', 'postage', 'packaging', 'commission paid',
      'job costs', 'project costs', 'production',
    ],
    category: 'cost_of_sales', confidence: 0.85,
  },
  // Revenue
  {
    patterns: [
      'revenue', 'income', 'sales', 'fee income', 'fees earned',
      'turnover', 'billings', 'invoiced', 'commission income',
      'service income', 'consulting income', 'management charge', 'retainer',
    ],
    category: 'revenue', confidence: 0.85,
  },
  // Staff costs (non-owner)
  {
    patterns: [
      'staff costs', 'staff wages', 'salaries', 'wages',
      'payroll', 'national insurance', 'employers ni', 'ni contributions',
      'staff pension', 'auto enrolment', 'workplace pension',
      'paye', 'employee', 'recruitment', 'training costs',
      'staff training', 'staff benefits', 'holiday pay', 'sick pay',
      'maternity', 'paternity', 'apprenticeship levy', 'agency staff',
    ],
    category: 'staff_costs', confidence: 0.85,
  },
  // Premises
  {
    patterns: [
      'rent', 'rates', 'business rates', 'utilities',
      'electricity', 'gas', 'water', 'office costs', 'premises',
      'property costs', 'lease', 'cleaning', 'maintenance', 'repairs',
      'buildings insurance', 'security', 'waste', 'service charge',
      'workspace', 'building',
    ],
    category: 'premises', confidence: 0.85,
  },
  // Marketing
  {
    patterns: [
      'marketing', 'advertising', 'promotion', 'social media',
      'website costs', 'seo', 'ppc', 'google ads', 'pr costs',
      'public relations', 'events', 'sponsorship', 'branding',
      'graphic design', 'print', 'photography', 'video production',
      'campaign', 'lead generation', 'content', 'copywriting',
      'email marketing', 'client entertainment', 'hospitality',
    ],
    category: 'marketing', confidence: 0.85,
  },
  // Professional fees
  {
    patterns: [
      'accountancy', 'accounting fees', 'legal fees',
      'legal costs', 'solicitor', 'professional fees', 'consulting fees',
      'consultancy', 'advisory fees', 'audit fees', 'bookkeeping',
      'payroll bureau', 'compliance costs', 'filing fees',
      'companies house', 'patent', 'trademark', 'due diligence',
    ],
    category: 'professional_fees', confidence: 0.85,
  },
  // IT & Technology
  {
    patterns: [
      'software', 'saas', 'software subscription',
      'cloud services', 'xero', 'microsoft', 'google workspace',
      'office 365', 'hosting', 'domain', 'it support', 'computer',
      'hardware', 'telephone', 'broadband', 'mobile phone',
      'technology', 'licence', 'license', 'apps', 'crm', 'telecoms',
      'internet', 'server', 'backup', 'cyber', 'antivirus',
      'it maintenance', 'equipment', 'subscription fees',
    ],
    category: 'it_technology', confidence: 0.85,
  },
  // Vehicles & Travel
  {
    patterns: [
      'vehicle', 'car costs', 'van costs', 'fuel', 'mileage',
      'parking', 'travel', 'transport', 'motor expenses', 'fleet',
      'hire car', 'taxi', 'train', 'flights', 'air travel',
      'accommodation', 'hotel', 'subsistence', 'client entertaining',
      'business travel', 'uber', 'vehicle insurance', 'road tax',
      'mot', 'servicing',
    ],
    category: 'vehicles', confidence: 0.85,
  },
  // Ignore (residual P&L items that should still be filtered out)
  {
    patterns: [
      'vat control', 'vat account', 'inter-company', 'intercompany',
      'suspense', 'clearing account', 'control account',
      'opening balance', 'retained earnings', 'share capital',
      'retained profit', 'corporation tax payable', 'paye control',
      'rounding',
    ],
    category: 'ignore', confidence: 0.90,
  },
];
export interface SuggestionResult {
  category: FFMCategory;
  confidence: number; // 0–1
  source: 'type' | 'code' | 'keyword' | 'default';
}
export function suggestCategory(
  name: string,
  type: string,
  code: string,
): SuggestionResult {
  const nameLower = (name || '').toLowerCase();
  const typeUpper = (type || '').toUpperCase();
  // Layer 0: Balance-sheet type guard — these are not P&L items.
  // Returned with very high confidence so they short-circuit all other logic.
  if (BALANCE_SHEET_TYPES.includes(typeUpper)) {
    return { category: 'ignore', confidence: 0.98, source: 'type' };
  }
  // Layer 1: Exact Xero P&L type match (high confidence).
  if (TYPE_MAP[typeUpper]) {
    return { category: TYPE_MAP[typeUpper], confidence: 0.95, source: 'type' };
  }
  // Layer 2: Keyword matching on account name (case-insensitive, both sides).
  // Find the best (highest-confidence) match across all keyword groups.
  let best: SuggestionResult | null = null;
  for (const { patterns, category, confidence } of KEYWORD_MAP) {
    for (const pattern of patterns) {
      if (nameLower.includes(pattern.toLowerCase())) {
        if (!best || confidence > best.confidence) {
          best = { category, confidence, source: 'keyword' };
        }
        break;
      }
    }
  }
  if (best) return best;
  // Layer 3: Code range fallback (standard COA ranges).
  const byCode = suggestByCode(code);
  if (byCode) {
    return { category: byCode, confidence: 0.70, source: 'code' };
  }
  // Default fallback — low confidence, requires human review.
  return { category: 'other_operating', confidence: 0.40, source: 'default' };
}
// ─── P&L grouping for S2 screens ─────────────────────────────────────────────
export const PL_GROUPS = [
  {
    key: 'revenue',
    label: 'Revenue',
    categories: ['revenue'] as FFMCategory[],
    sign: 1,
  },
  {
    key: 'cost_of_sales',
    label: 'Cost of Sales',
    categories: ['cost_of_sales'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'staff',
    label: 'Staff Costs',
    categories: ['staff_costs', 'owner_salary'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'owner_pension',
    label: 'Owner Pension',
    categories: ['owner_pension'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'premises',
    label: 'Premises',
    categories: ['premises'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    categories: ['marketing'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'professional_fees',
    label: 'Professional Fees',
    categories: ['professional_fees'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'it_technology',
    label: 'IT & Technology',
    categories: ['it_technology'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'vehicles',
    label: 'Vehicles & Travel',
    categories: ['vehicles'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'other_operating',
    label: 'Other Operating',
    categories: ['other_operating'] as FFMCategory[],
    sign: -1,
  },
  {
    key: 'finance',
    label: 'Finance & Depreciation',
    categories: ['finance_costs', 'depreciation'] as FFMCategory[],
    sign: -1,
  },
] as const;
